import { LANGUAGES, DEFAULT_LANGUAGE } from "@/lib/languages";

export type Props = {
  lang: string;
  initialCart?: unknown | null;
};

export type AddressForm = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  nif: string;
};

export type Me = {
  id: number;
  email: string;
  display_name: string;
  billing?: Partial<AddressForm> & {
    company?: string;
  };
};

export type PaymentMethod =
  | "cod"
  | "mbway_ifthen_for_woocommerce"
  | "multibanco_ifthen_for_woocommerce"
  | "creditcard_ifthen_for_woocommerce"
  | "free_ticket";

export type MoneyObject = {
  formatted?: string;
  value?: number | string;
};

export type MoneyLike = MoneyObject | string | number | null | undefined;

export type CurrencyMeta = {
  currency_code?: string;
  currency_minor_unit?: number;
};

export type Totals = CurrencyMeta & {
  total_price?: MoneyLike;
  total?: MoneyLike;
  total_items?: MoneyLike;
  total_discount?: MoneyLike;
  subtotal?: MoneyLike;
  subtotal_price?: MoneyLike;
  items_total?: MoneyLike;
};

export type ItemMetaKV = {
  key?: string;
  name?: string;
  value?: string | number;
};

export type MetaDataKV = {
  display_key?: string;
  key?: string;
  display_value?: string | number;
  value?: string | number;
};

export type CartItemTotals = CurrencyMeta & {
  line_total?: MoneyLike;
  line_subtotal?: MoneyLike;
  total?: MoneyLike;
};

export type CartItemPrices = CurrencyMeta & {
  line_total?: MoneyLike;
  price?: MoneyLike;
};

export type CartItem = {
  key?: string | number;
  id?: string | number;
  name?: string;
  permalink?: string;
  quantity?: number | string;
  item_data?: ItemMetaKV[];
  meta_data?: MetaDataKV[];
  variation?: Record<string, string | number | null | undefined>;
  totals?: CartItemTotals;
  prices?: CartItemPrices;
};

export type Coupon = {
  code?: string;
  coupon?: string;
  coupon_code?: string;
  name?: string;
};

export type Cart = {
  items?: CartItem[];
  items_count?: number | string;
  totals?: Totals;
  coupons?: Coupon[];
};

export type PaymentDetails = {
  key: string;
  value: string;
};

export type SuccessOrder = {
  orderId?: number | string | null;
  orderNumber?: number | string | null;
  orderKey?: string | null;
  status?: string | null;
  email?: string | null;
  total?: string | null;
  createdVia?: string | null;
  items?: CartItem[];
  subtotal?: string | null;
  paymentDetails?: PaymentDetails[];
  redirectUrl?: string | null;
};

export const EMPTY_ADDR: AddressForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  postcode: "",
  country: "GB",
  nif: "",
};

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

export function toInt(n: unknown, fallback = 0) {
  const x = Number(n);
  return Number.isFinite(x) ? x : fallback;
}

function safeString(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

export function formatISODateToDDMMYYYY(iso: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

export function resolveLocaleFromLang(lang: string) {
  const raw = (lang || "").trim().toLowerCase();
  const found = LANGUAGES.find(
    (l) =>
      l.polylangCode.toLowerCase() === raw ||
      l.preferredBy.some((p) => p.toLowerCase() === raw)
  );
  return found?.polylangLocale ?? DEFAULT_LANGUAGE.polylangLocale;
}

export function getMoneyPrimitive(v: unknown): string | number | null {
  if (typeof v === "string" && v.trim()) return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (isRecord(v)) {
    const raw = v.value;
    if (typeof raw === "string" && raw.trim()) return raw.trim();
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    const formatted = v.formatted;
    if (typeof formatted === "string" && formatted.trim()) return formatted.trim();
  }
  return null;
}

export function isAlreadyFormattedMoney(value: string) {
  return /[€$£¥₹]|eur|usd|gbp|jpy|aud|cad|chf|sek|nok|dkk|pln/i.test(value);
}

export function formatWooMinorMoney(value: string | number, currencyCode: string, minorUnit: number, locale: string) {
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) return "—";
  const major = numeric / Math.pow(10, minorUnit);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: minorUnit,
    maximumFractionDigits: minorUnit,
  }).format(major);
}

export function formatMoneyLike(value: MoneyLike, currencyCode: string | undefined, minorUnit: number | undefined, locale: string): string | null {
  const primitive = getMoneyPrimitive(value);
  if (primitive === null) return null;
  if (typeof primitive === "string" && isAlreadyFormattedMoney(primitive)) return primitive;
  if (!currencyCode || typeof minorUnit !== "number") {
    return typeof primitive === "string" || typeof primitive === "number" ? String(primitive) : null;
  }
  return formatWooMinorMoney(primitive, currencyCode, minorUnit, locale);
}

export function moneyFromTotals(totals: Totals | undefined, locale = "en-GB") {
  if (!totals) return null;
  return (
    formatMoneyLike(totals.total_price, totals.currency_code, totals.currency_minor_unit, locale) ??
    formatMoneyLike(totals.total, totals.currency_code, totals.currency_minor_unit, locale) ??
    null
  );
}

export function moneyFromSubtotal(totals: Totals | undefined, locale = "en-GB") {
  if (!totals) return null;
  return (
    formatMoneyLike(totals.total_items, totals.currency_code, totals.currency_minor_unit, locale) ??
    formatMoneyLike(totals.subtotal, totals.currency_code, totals.currency_minor_unit, locale) ??
    formatMoneyLike(totals.subtotal_price, totals.currency_code, totals.currency_minor_unit, locale) ??
    formatMoneyLike(totals.items_total, totals.currency_code, totals.currency_minor_unit, locale) ??
    null
  );
}

export function lineMoney(it: CartItem, locale = "en-GB") {
  return (
    formatMoneyLike(it.totals?.line_total, it.totals?.currency_code ?? it.prices?.currency_code, it.totals?.currency_minor_unit ?? it.prices?.currency_minor_unit, locale) ??
    formatMoneyLike(it.totals?.total, it.totals?.currency_code ?? it.prices?.currency_code, it.totals?.currency_minor_unit ?? it.prices?.currency_minor_unit, locale) ??
    formatMoneyLike(it.prices?.line_total, it.prices?.currency_code ?? it.totals?.currency_code, it.prices?.currency_minor_unit ?? it.totals?.currency_minor_unit, locale) ??
    formatMoneyLike(it.prices?.price, it.prices?.currency_code ?? it.totals?.currency_code, it.prices?.currency_minor_unit ?? it.totals?.currency_minor_unit, locale) ??
    "—"
  );
}

function formatBookingTime24(value: unknown): string {
  const raw = safeString(value);
  if (!raw) return "";

  const match = /^(\d{1,2})(?::(\d{2}))?\s*([ap]m)?$/i.exec(raw);
  if (!match) return raw.replace(/\s*([ap]m)\s*$/i, "").trim();

  let hours = Number(match[1]);
  const minutes = Number(match[2] ?? "0");
  const meridiem = match[3]?.toLowerCase();

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return raw;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return raw;

  if (meridiem === "pm" && hours < 12) hours += 12;
  if (meridiem === "am" && hours === 12) hours = 0;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function looksLikeBookingTime(value: string): boolean {
  return /^\d{1,2}(?::\d{2})?\s*([ap]m)?$/i.test(value.trim());
}

function parseStructuredSlot(item: Pick<CartItem, "item_data">): { label: string; date: string; time: string } | null {
  const rows = Array.isArray(item.item_data) ? item.item_data : [];
  if (rows.length === 0) return null;

  const timeSlotRow = rows.find((row) => safeString(row?.key).toLowerCase() === "time slot");
  const raw = safeString(timeSlotRow?.value);
  if (!raw) return null;

  const parts = raw.split("•").map((part) => part.trim()).filter(Boolean);
  const firstPart = parts[0] ?? "";
  const isoDate = parts.find((part) => /^\d{4}-\d{2}-\d{2}$/.test(part)) ?? "";

  const rawTime =
    [...parts].reverse().find((part) => /^\d{1,2}:\d{2}(\s*[ap]m)?$/i.test(part)) ??
    (looksLikeBookingTime(firstPart) ? firstPart : "");

  const time = formatBookingTime24(rawTime);
  const label = firstPart && !looksLikeBookingTime(firstPart) ? firstPart : "";

  return {
    label,
    date: isoDate ? formatISODateToDDMMYYYY(isoDate) : "",
    time,
  };
}

export function itemMetaLines(it: CartItem): string[] {
  const structured = parseStructuredSlot(it);
  if (structured) {
    const line = [structured.date, structured.time].filter(Boolean).join(" • ");
    return [structured.label, line].filter(Boolean);
  }
  const out: string[] = [];
  const a = it.item_data;
  if (Array.isArray(a)) {
    for (const m of a) {
      const key = safeString(m?.key ?? m?.name ?? "");
      const value = safeString(m?.value ?? "");
      if (!key || !value) continue;
      if (key.toLowerCase() === "time slot") continue;
      if (key.toLowerCase() === "slot") continue;
      if (key.toLowerCase() === "date" && /^\d{4}-\d{2}-\d{2}$/.test(value)) { out.push(`Date: ${formatISODateToDDMMYYYY(value)}`); continue; }
      out.push(`${key}: ${value}`);
    }
  }
  const b = it.meta_data;
  if (Array.isArray(b)) {
    for (const m of b) {
      const key = safeString(m?.display_key ?? m?.key ?? "");
      const value = safeString(m?.display_value ?? m?.value ?? "");
      if (!key || !value) continue;
      out.push(`${key}: ${value}`);
    }
  }
  const c = it.variation;
  if (c && typeof c === "object") {
    for (const [rawKey, rawValue] of Object.entries(c)) {
      if (rawValue === null || rawValue === undefined || rawValue === "") continue;
      const key = String(rawKey).replace(/^attribute_/, "");
      out.push(`${key}: ${String(rawValue)}`);
    }
  }
  return Array.from(new Set(out.map((s) => s.trim()))).filter(Boolean).slice(0, 3);
}

export function looksLikeEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function formatPaymentDetailKey(key: string): string {
  const map: Record<string, string> = {
    entity: "Entity", reference: "Reference", amount: "Amount", expiry_date: "Valid until", terminal: "Terminal",
    entidade: "Entity", referencia: "Reference", valor: "Amount", data_expiracao: "Valid until", data_validade: "Valid until", validade: "Valid until",
  };
  const normalised = key.trim().toLowerCase();
  return map[normalised] ?? key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");
}

export function extractOrderRef(json: unknown): SuccessOrder {
  const rec = isRecord(json) ? json : null;
  const data = rec && isRecord(rec.data) ? rec.data : null;
  const order = rec && isRecord(rec.order) ? rec.order : null;
  const paymentResult = rec && isRecord(rec.payment_result) ? rec.payment_result : null;

  const orderId = (rec?.id as unknown) ?? (rec?.order_id as unknown) ?? (data?.id as unknown) ?? (order?.id as unknown) ?? null;
  const orderNumber = (rec?.number as unknown) ?? (rec?.order_number as unknown) ?? (data?.number as unknown) ?? (order?.number as unknown) ?? orderId ?? null;
  const orderKey = (rec?.order_key as unknown) ?? (rec?.key as unknown) ?? (data?.order_key as unknown) ?? (order?.order_key as unknown) ?? null;
  const total = (rec ? getMoneyPrimitive(rec.total) : null) ?? (data ? getMoneyPrimitive(data.total) : null) ?? (order ? getMoneyPrimitive(order.total) : null) ?? null;
  const subtotal = (rec ? getMoneyPrimitive(rec.subtotal) : null) ?? (data ? getMoneyPrimitive(data.subtotal) : null) ?? null;
  const status = typeof rec?.status === "string" ? rec.status : null;
  const currencyCode = typeof rec?.currency === "string" ? rec.currency : undefined;
  const minorUnit = typeof rec?.currency_minor_unit === "number" ? rec.currency_minor_unit : undefined;

  const billing =
    (rec && isRecord(rec.billing) ? rec.billing : null) ??
    (rec && isRecord(rec.billing_address) ? rec.billing_address : null) ??
    (data && isRecord(data.billing) ? data.billing : null) ??
    null;

  const email = billing && typeof billing.email === "string" && billing.email.trim() ? billing.email : null;
  const createdViaRaw = (rec?.payment_method as unknown) ?? (data?.payment_method as unknown) ?? (order?.payment_method as unknown) ?? null;

  const redirectUrl = paymentResult && typeof paymentResult.redirect_url === "string" && paymentResult.redirect_url.trim()
    ? paymentResult.redirect_url.trim() : null;

  const rawDetails = paymentResult && Array.isArray(paymentResult.payment_details) ? paymentResult.payment_details : [];
  const paymentDetails: PaymentDetails[] = (rawDetails as unknown[])
    .filter((d): d is Record<string, unknown> => isRecord(d) && typeof (d as Record<string, unknown>).key === "string")
    .map((d) => ({ key: String(d.key), value: String(d.value ?? "") }))
    .filter((d) => d.key.trim() !== "" && d.value.trim() !== "" && !["result","redirect"].includes(d.key.trim().toLowerCase()));

  const rawItems = rec && Array.isArray(rec.items) ? rec.items : [];
  const items: CartItem[] = (rawItems as unknown[])
    .filter((it): it is Record<string, unknown> => isRecord(it))
    .map((it) => ({
      key: it.key as string | number | undefined,
      id: it.id as string | number | undefined,
      name: typeof it.name === "string" ? it.name : undefined,
      permalink: typeof it.permalink === "string" ? it.permalink : undefined,
      quantity: it.quantity as number | string | undefined,
      totals: isRecord(it.totals) ? (it.totals as CartItemTotals) : undefined,
      meta_data: Array.isArray(it.meta_data) ? (it.meta_data as MetaDataKV[]) : undefined,
    }));

  const locale = "en-GB";
  const formattedTotal = total !== null
    ? (formatMoneyLike(total, currencyCode, minorUnit, locale) ?? String(total))
    : null;
  const formattedSubtotal = subtotal !== null
    ? (formatMoneyLike(subtotal, currencyCode, minorUnit, locale) ?? String(subtotal))
    : null;

  return {
    orderId: orderId !== null && orderId !== undefined ? (orderId as number | string) : null,
    orderNumber: orderNumber !== null && orderNumber !== undefined ? (orderNumber as number | string) : null,
    orderKey: typeof orderKey === "string" ? orderKey : null,
    total: formattedTotal,
    subtotal: formattedSubtotal,
    status,
    email,
    createdVia: typeof createdViaRaw === "string" ? createdViaRaw : null,
    items: items.length > 0 ? items : undefined,
    paymentDetails: paymentDetails.length > 0 ? paymentDetails : undefined,
    redirectUrl,
  };
}

export function coerceCart(v: unknown): Cart | null {
  if (!isRecord(v)) return null;
  return {
    items: Array.isArray(v.items) ? (v.items as CartItem[]) : [],
    items_count: v.items_count as number | string | undefined,
    totals: isRecord(v.totals) ? (v.totals as Totals) : undefined,
    coupons: Array.isArray(v.coupons) ? (v.coupons as Coupon[]) : [],
  };
}